import { useMemo } from 'react'

import { type ComicCharacterCard,normalizeCharacterCards } from '../../lib/comicCharacters'
import { characterLinePreview, syncLinesWithCharacterIds } from '../../lib/comicPanelCharacterLines'
import type { ComicPanelCharacterLine } from '../../lib/comicTypes'
import { ChromeCollapsibleSection } from '../ChromeCollapsibleSection'
import { ChromeComposerTextarea } from '../ChromeComposerTextarea'
import { HorizontalChipStrip } from '../HorizontalChipStrip'
import { ComicCharacterCardPreviewTile } from './ComicCharacterCardPreviewTile'

type Props = {
  angular: boolean
  characters: ComicCharacterCard[]
  selectedIds: string[]
  lines: ComicPanelCharacterLine[]
  expandedCharacterId: string | null
  onChangeSelectedIds: (ids: string[]) => void
  onChangeLines: (lines: ComicPanelCharacterLine[]) => void
  onExpandCharacter: (id: string | null) => void
}

export function ComicPanelCharacterEditor({
  angular,
  characters,
  selectedIds,
  lines,
  expandedCharacterId,
  onChangeSelectedIds,
  onChangeLines,
  onExpandCharacter,
}: Props) {
  const cards = normalizeCharacterCards(characters)
  const cardById = useMemo(() => new Map(cards.map((c) => [c.id, c])), [cards])

  const toggleCharacter = (id: string) => {
    if (selectedIds.includes(id)) {
      const nextIds = selectedIds.filter((x) => x !== id)
      onChangeSelectedIds(nextIds)
      onChangeLines(lines.filter((l) => l.characterId !== id))
      if (expandedCharacterId === id) {
        onExpandCharacter(nextIds[0] ?? null)
      }
      return
    }
    const nextIds = [...selectedIds, id]
    onChangeSelectedIds(nextIds)
    onChangeLines(syncLinesWithCharacterIds(nextIds, lines))
    onExpandCharacter(id)
  }

  const patchLine = (characterId: string, patch: Partial<Pick<ComicPanelCharacterLine, 'speech' | 'thought'>>) => {
    onChangeLines(
      lines.map((l) => (l.characterId === characterId ? { ...l, ...patch } : l)),
    )
  }

  if (cards.length === 0) {
    return <p className="text-xs text-ink-soft">尚无角色</p>
  }

  const orderedSelected = selectedIds
    .map((id) => cardById.get(id))
    .filter((c): c is ComicCharacterCard => Boolean(c))

  return (
    <div className="space-y-3">
      <HorizontalChipStrip
        className="pb-1"
        trackClassName="items-stretch gap-3 px-0.5"
        role="list"
        aria-label="选择出场角色"
      >
        {cards.map((card) => (
          <ComicCharacterCardPreviewTile
            key={card.id}
            angular={angular}
            card={card}
            selected={selectedIds.includes(card.id)}
            selectionMode="multi"
            onClick={() => toggleCharacter(card.id)}
          />
        ))}
      </HorizontalChipStrip>

      {orderedSelected.length > 0 ? (
        <div className="space-y-2">
          {orderedSelected.map((card) => {
            const line = lines.find((l) => l.characterId === card.id)
            const speech = line?.speech ?? ''
            const thought = line?.thought ?? ''
            const open = expandedCharacterId === card.id
            const preview = characterLinePreview(line ?? { characterId: card.id, speech: '', thought: '' })
            return (
              <ChromeCollapsibleSection
                key={card.id}
                angular={angular}
                title={card.name.trim() || '未命名'}
                subtitle={open ? undefined : preview}
                open={open}
                onToggle={() => onExpandCharacter(open ? null : card.id)}
              >
                <div className="space-y-3">
                  <label className="block text-xs font-semibold text-ink">
                    台词
                    <ChromeComposerTextarea
                      angular={angular}
                      variant="form"
                      value={speech}
                      onChange={(e) => patchLine(card.id, { speech: e.target.value })}
                      placeholder={`${card.name}说出口的对白`}
                      maxLength={500}
                      textareaClassName="min-h-[2.75rem]"
                    />
                  </label>
                  <label className="block text-xs font-semibold text-ink">
                    心声
                    <ChromeComposerTextarea
                      angular={angular}
                      variant="form"
                      value={thought}
                      onChange={(e) => patchLine(card.id, { thought: e.target.value })}
                      placeholder={`${card.name}的内心独白`}
                      maxLength={500}
                      textareaClassName="min-h-[2.75rem]"
                    />
                  </label>
                </div>
              </ChromeCollapsibleSection>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
